/* eslint-disable radix */
/* eslint-disable class-methods-use-this */
/* eslint-disable no-await-in-loop */
import cloneDeep from "lodash/cloneDeep"
import isEqual from "lodash/isEqual"
import { PageConfigModel } from "../models/pageConfig"
import ComponentService from "./component"
import { Service } from "./Service"
import StoreService from "./store"
import DialogService from "./dialog"
import { DialogType } from "../models/dialog"
import { ProviderService } from "./provider"
import { NavigationParam } from "../models/navigationParam"

class NavigationService extends Service {
    private static instance: NavigationService
    private routingListeners: Array<
        ((pageConfig: PageConfigModel, params: any) => void) | undefined
    > = []
    private navParam: NavigationParam
    private stack: Array<{
        page: PageConfigModel
        snapshot: any
        params?: any
    }> = []

    private store: StoreService

    private state:
        | { page: PageConfigModel; snapshot: any; params?: any }
        | undefined

    private popstateCallBack?: (e: PopStateEvent) => void

    constructor() {
        super()
        // set le nom du service
        this.name = "NavigationService"
        // ajoute toute les methodes du service au tableau de mapping
        this.serviceMap = [
            { key: "navigateTo", fct: this.navigateTo },
            { key: "refreshState", fct: this.refreshState },
            { key: "loadFirstPage", fct: this.loadFirstPage },
            { key: "exit", fct: this.exit },
            { key: "back", fct: this.back },
            { key: "refreshPage", fct: this.refreshPage },
            { key: "clearStack", fct: this.clearStack },
            { key: "getBackState", fct: this.getBackState },
        ]
        this.store = StoreService.getInstance()
        const navParamService = ProviderService.getInstance().get(
            "NavigationParamService"
        )
        this.navParam = navParamService.getInstance()
    }

    /**
     * Récuperation de l'instance
     */
    public static getInstance(): NavigationService {
        if (!this.instance) {
            this.instance = new NavigationService()
        }
        return this.instance
    }

    /**
     * init
     */
    public init(): void {
        // abonnement aux événements de navigation du navigateur
        this.popstateCallBack = (e: PopStateEvent) => {
            this.popstateCallback(e)
        }
        window.addEventListener("popstate", this.popstateCallBack, false)
    }

    /**
     * loadFirstPage en fonction de la session charge la bonne page
     */
    public async loadFirstPage(): Promise<void> {
        // récuperation de la config de la page à charger
        const parsedUrl = this.navParam.get()
        const customLandingPage =
            await StoreService.getInstance().persistant.get("landingPageConfig")
        const token = await StoreService.getInstance().persistant.get("token")
        let defaultPage: string | undefined
        if (customLandingPage && !token) defaultPage = customLandingPage
        else if (process.env.REACT_APP_DEFAULT_PAGE && !token)
            defaultPage = process.env.REACT_APP_DEFAULT_PAGE
        else if (process.env.REACT_APP_DEFAULT_HOME_PAGE && token)
            defaultPage = process.env.REACT_APP_DEFAULT_HOME_PAGE
        // on a une page déjà demandée dans l'url

        if (
            parsedUrl &&
            parsedUrl.pageName !== "" &&
            process.env.REACT_APP_DEFAULT_PAGE &&
            parsedUrl.pageName !== process.env.REACT_APP_DEFAULT_PAGE && // si la page est differente de celle d'accueil
            parsedUrl.pageName !== "login" && // si la page est differente de la page de login
            parsedUrl.pageName !== customLandingPage
        ) {
            ComponentService.getInstance()
                .getPageConfig(this.navParam.get().pageName)
                .then(
                    (pageConfig: PageConfigModel | undefined) => {
                        const { params } = this.navParam.get()
                        this.navParam.clear()

                        return pageConfig
                            ? this.navigateTo(pageConfig, params)
                            : undefined
                    },
                    (err) => {
                        if (err?.response?.status === 404) {
                            this.navigateTo("notFound")
                        }

                        console.error("App init fail, reseting...", err)
                    }
                )

            // on charge la pageConfig de la page par défaut de l'env
        } else if (defaultPage) {
            // on reset l'URL pour ne pas avoir de reste de route parasite
            this.navParam.clear()
            ComponentService.getInstance()
                .getPageConfig(defaultPage)
                .then(
                    (pageConfig: PageConfigModel | undefined) => {
                        return pageConfig
                            ? this.navigateTo(pageConfig)
                            : undefined
                    },
                    (err) => {
                        // si 404 page not found
                        if (err?.response?.status === 404) {
                            this.navigateTo("notFound")
                        }
                        console.error("App init fail", err)
                    }
                )
        } else {
            console.error("impossible de lancer l'application")
        }
    }

    /**
     * clearListeners
     */
    public clearListeners(): void {
        // déabonnement aux événements de navigation du navigateur
        if (this.popstateCallBack)
            window.removeEventListener("popstate", this.popstateCallBack, false)
    }

    /**
     * Function appelée lors du clique sur le bouton back du navigateur
     * @param event
     */
    private popstateCallback = async (event: PopStateEvent) => {
        event.preventDefault()
        const url = this.navParam.get()
        const stackLimit = process.env.STACK_LIMIT
            ? process?.env?.STACK_LIMIT
            : "20"

        // on test si l'url courant est different de celui actuel (= changement browser)
        if (this.state?.page.name !== url.pageName) {
            // si on a une stack avec un seul element et qu'on à pas atteind la limite de taille
            // utilisation back natif
            const lastState = this.stack[this.stack.length - 2]

            if (
                this.stack.length > 1 &&
                this.stack.length < parseInt(stackLimit) &&
                lastState.page.name === url.pageName
            ) {
                // si le state précédent = le courant on a fait un back browser
                this.back()
            } else {
                // changement de page dans la barre de navigation
                const currentUrlUrl = this.navParam.get()

                // test si on a remis login par défaut il faut supprimer landingPageConfig
                // si elle existe dans le persistant
                if (currentUrlUrl?.pageName === "login")
                    await StoreService.getInstance().persistant.delete(
                        "landingPageConfig"
                    )
                this.navigateTo(currentUrlUrl.pageName, currentUrlUrl.params)
            }
        }
        // on change le hash vers le state courant
        if (this.state?.page.name && this.state?.page.name !== url.pageName) {
            this.navParam.set(this.state.page.name, this.state?.params)
            return
        }

        // envoie une notifiction à tous les listeners
        this.routingListeners.forEach((fct) => {
            if (this.state?.page && fct) {
                fct(this.state.page, this.state.params)
            }
        })
        if (this.stack.length > parseInt(stackLimit)) this.stack.shift()
    }

    /**
     * getLastState récupère le state en cours
     */
    public getLastState(): {
        page: PageConfigModel
        snapshot: any
        params?: any
    } {
        return this.stack[this.stack.length - 1]
    }

    /**
     * getBackState récupère le state qui serait utilisé par la méthode back()
     * (l'état précédent dans la stack,sans effectuer le back).
     */
    public getBackState():
        | {
              page: PageConfigModel
              snapshot: any
              params?: any
          }
        | undefined {
        if (this.stack.length > 1) {
            return this.stack[this.stack.length - 2]
        }
        return undefined
    }

    /**
     * clearStack clear toute la stack de l'app
     */
    public clearStack(): void {
        this.stack = []
    }

    /**
     * navigateTo clear la page courrante et charge un autre PageConfig
     * @param page nouveau nom de page à charger ou objet pageConfig
     * @param params object qui contient les paramètres à mettre en queryParams (ex. {param1: xxx, params2: yyy} -> param1=xxx&params2=yyy)
     * @param snapshot état du store à mettre si fourni
     */
    public async navigateTo(
        page: string | PageConfigModel,
        params?: unknown,
        snapshot?: unknown
    ): Promise<void> {
        // copy the state
        const oldState: any = cloneDeep(this.state)
        let pageConfig: PageConfigModel | undefined
        // get la config de la nouvelle page
        if (typeof page === "string") {
            pageConfig = await ComponentService.getInstance().getPageConfig(
                page
            )
        } else pageConfig = page

        if (pageConfig) {
            // sauvegarde l'état du store
            const currentSnap = this.store.local.snapshot()
            const currentState =
                this.stack.length > 0
                    ? this.stack[this.stack.length - 1]
                    : undefined
            // mise à jour du state courant avant le changement de page
            if (currentState) currentState.snapshot = currentSnap

            /* on push le nouveau state que si on tente de charger une page differente
             * de celle courante ou bien si les config n'indique pas que cette page doit
             * être ignorée dans la navigation
             */
            if (
                !(
                    pageConfig.config?.noNavigationStack ||
                    currentState?.page?.name === pageConfig.name
                )
            ) {
                const newState = {
                    page: pageConfig,
                    // si on a l'option noStateClear on garde l'ancien State dans le nouveau
                    snapshot: oldState?.page?.config?.noStateClear
                        ? currentSnap
                        : snapshot || {},
                    params,
                }
                // push du nouveau state
                this.stack.push(newState)
                // maj du state courrant apres push
                this.state = newState
                // en cas de nouvelle page on restore le SharedData
                this.store.local.restore(newState.snapshot)
            } else {
                // maj du state courrant
                this.state = { page: pageConfig, snapshot, params }
            }
            // on change le hash (triggger de l'event popstate)
            this.navParam.set(pageConfig.name, params)
            // si les params de l'URL on été modifié on émet on event de type REFRESH
            // et que les nom de page sont les même (si ils sont different pas besoin de refresh)
            if (
                (this.state?.params &&
                    this?.state?.page?.name === oldState?.page?.name &&
                    !isEqual(oldState.params, this.state.params)) ||
                !oldState
            ) {
                this.stack[
                    this.stack.findIndex(
                        (obj) => obj.page.name === this.state?.page.name
                    )
                ] = this.state
                DialogService.getInstance().send({
                    type: DialogType.REFRESH,
                    sender: "navigationService",
                })
            }
        }
    }

    /**
     * refreshState refresh la pageConfig actuelle
     */
    public refreshState(): Promise<void> {
        return DialogService.getInstance().send({
            type: DialogType.REFRESH,
            sender: "navigationService",
        })
    }

    /**
     * back clear la page courrante et charge un autre PageConfig
     */
    public back(): any {
        // si la pile de navigation a plus de deux state
        if (this.stack.length > 1) {
            this.stack.pop()
            const nextState = this.stack[this.stack.length - 1]
            this.state = nextState

            // on change le hash
            this.navParam.set(this.state.page.name, this.state.params)
        }
    }

    /**
     * exit clear la page courante et charge la page par défaut REACT_APP_DEFAULT_PAGE
     */
    public async exit(): Promise<any> {
        // on vide la stack
        this.stack = []
        // gestion de custom login page
        let loginPage: any
        const landingPageConfig =
            await StoreService.getInstance().persistant.get("landingPageConfig")
        // on choisir entre la landingPage par defaut ou bien celle custom
        if (landingPageConfig) loginPage = landingPageConfig
        else if (process.env.REACT_APP_DEFAULT_PAGE)
            loginPage = process.env.REACT_APP_DEFAULT_PAGE

        // si on a une page on la charge
        if (loginPage) {
            ComponentService.getInstance()
                .getPageConfig(loginPage)
                .then(
                    (pageConfig: PageConfigModel | undefined) => {
                        return pageConfig
                            ? this.navigateTo(pageConfig)
                            : undefined
                    },
                    (err) => {
                        console.error("Routing to default page fail", err)
                    }
                )
        }
    }

    public getParams(): any {
        return this.state?.params
    }

    /**
     * isAppBackAvailable
     */
    public isAppBackAvailable(): boolean {
        return this.stack.length > 1
    }

    public isStackEmpty(): boolean {
        return this.stack.length === 0
    }

    /**
     * listenRoutingEvents S'abonne aux event de routing
     * @param callback: (pageConfig: PageConfig) => void
     * @returns L'index du tableau où l'event à été stocké
     */
    public listenRoutingEvents(
        callback: (pageConfig: PageConfigModel, params: any) => void
    ): number {
        let reusedIndex = -1
        // on essai de trouver une place dans le tableau
        this.routingListeners.forEach((item: any, index: number) => {
            if (item === undefined) {
                this.routingListeners[index] = callback
                reusedIndex = index
            }
        })

        if (reusedIndex > -1) return reusedIndex

        this.routingListeners.push(callback)
        return this.routingListeners.length - 1
    }

    /**
     * listenRoutingEvents S'abonne aux event de routing
     * @param callback: (pageConfig: PageConfig) => void
     * @returns L'index du tableau où l'event à été stocké
     */
    public unsubscribeRoutingEvents(eventIndex: number): void {
        if (eventIndex > -1) this.routingListeners[eventIndex] = undefined
    }

    /**
     * Gère la redirection si le hash redir est présent sinon redirection vers HOME
     */
    public handleRedir() {
        const url = this.navParam.get()
        // il y a une redir a faire
        if (url && url.params && url.params.redir) {
            const newUrl = this.navParam.get(url.params.redir)
            this.navigateTo(newUrl.pageName, newUrl.params)
        } else {
            this.loadFirstPage()
        }
    }

    public refreshPage() {
        // refresh de la page avec js
        window.location.reload()
    }
}

export default NavigationService
